import type { AuthOidcAuthorizationRequiredResponse } from "@simbelapp/order-sdk";
import { AuthFlowEnum, AuthStrategy } from "~/utils/enums/auth.enums";
import type { IAuthDiscovery, IOidcConfiguration } from "~/utils/interfaces/oidc-interfaces";

export const OIDC_CALLBACK_PATH = "/auth/oidc/callback";
export const OIDC_LOGIN_PATH = "/auth/oidc/login";
export const OIDC_LOGOUT_PATH = "/auth/oidc/logout";
export const OIDC_SCOPE = "openid profile email orders";
export const AUTH_REQUIRED_MESSAGE = "Votre session a expiré. Veuillez vous reconnecter.";
export const OIDC_UNAVAILABLE_MESSAGE = "La connexion est indisponible. Contactez votre administrateur.";
// Orders' LOGIN_ATTEMPT_LIFETIME_SECONDS: past it, a 401 on the password is an expired ticket, not a wrong password.
export const OIDC_LOGIN_ATTEMPT_LIFETIME_MS = 10 * 60_000;

export class OidcDiscoveryUnavailableError extends Error {
  constructor() {
    super(OIDC_UNAVAILABLE_MESSAGE);
    this.name = "OidcDiscoveryUnavailableError";
  }
  
}

export function parseAuthDiscovery(value: unknown): IAuthDiscovery {
  const data = value as Partial<IAuthDiscovery> | null;
  if (
    !data ||
    !Object.values(AuthFlowEnum).includes(data.authFlow!) ||
    !Object.values(AuthStrategy).includes(data.authStrategy!) ||
    !data.companyId ||
    !data.status ||
    !Number.isInteger(data.authVersion)
  ) {
    throw new Error(OIDC_UNAVAILABLE_MESSAGE);
  }
  return data as IAuthDiscovery;
}

export function requiresOidcAuthorization(value: unknown): value is AuthOidcAuthorizationRequiredResponse {
  const data = value as Record<string, unknown> | null;
  if (data?.authFlow !== undefined || data?.authorizationRequired !== undefined) {
    if (
      data.authFlow !== AuthFlowEnum.OIDC ||
      data.authorizationRequired !== true ||
      typeof data.companyId !== "string"
    ) {
      throw new Error(OIDC_UNAVAILABLE_MESSAGE);
    }
    return true;
  }
  if (
    typeof data?.accessToken !== "string" ||
    !data.accessToken ||
    typeof data?.refreshToken !== "string" ||
    !data.refreshToken
  ) {
    throw new Error(AUTH_REQUIRED_MESSAGE);
  }
  return false;
}

function secureUrl(value: string): URL {
  const url = new URL(value);
  const local = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
  if (
    (url.protocol !== "https:" && !(local && url.protocol === "http:")) ||
    url.username ||
    url.password ||
    url.hash ||
    url.search
  ) {
    throw new Error(OIDC_UNAVAILABLE_MESSAGE);
  }
  return url;
}

/**
 * The issuer Teams talks to: its own origin when it proxies Orders' OIDC routes, else the API host.
 * `OIDC_SAME_ORIGIN` is set per environment together with Orders' flag: the discovery document's
 * issuer must equal this authority, so both sides move at the same deploy.
 */
export function oidcAuthority(config: { ORDERS_URL?: unknown; OIDC_SAME_ORIGIN?: unknown }, origin: string): string {
  const base = config.OIDC_SAME_ORIGIN === true ? origin : String(config.ORDERS_URL ?? "");
  return `${base.replace(/\/+$/, "")}/oidc`;
}

export function validateOidcConfiguration(config: IOidcConfiguration, origin: string): IOidcConfiguration {
  if (Object.values(config).some((value) => typeof value !== "string" || !value.trim()))
    throw new Error(OIDC_UNAVAILABLE_MESSAGE);
  const issuer = secureUrl(config.authority);
  const orders = secureUrl(config.ordersUrl);
  const callback = secureUrl(config.redirectUri);
  const logout = secureUrl(config.postLogoutRedirectUri);
  if (
    (issuer.origin !== orders.origin && issuer.origin !== origin) ||
    callback.origin !== origin ||
    logout.origin !== origin ||
    callback.pathname !== OIDC_CALLBACK_PATH ||
    logout.pathname !== OIDC_LOGOUT_PATH
  )
    throw new Error(OIDC_UNAVAILABLE_MESSAGE);
  return config;
}

export function validateCompletionUrl(value: string, authority: string, interaction: string): string {
  try {
    const url = new URL(value);
    if (
      url.origin !== new URL(authority).origin ||
      url.username ||
      url.password ||
      url.hash ||
      url.pathname !== `/auth/oidc/interactions/${encodeURIComponent(interaction)}/complete` ||
      !url.searchParams.get("proof") ||
      Array.from(url.searchParams.keys()).some((key) => key !== "proof")
    )
      throw new Error();
    return url.href;
  } catch {
    throw new Error(OIDC_UNAVAILABLE_MESSAGE);
  }
}

export function isOidcRoute(path: string): boolean {
  return [OIDC_LOGIN_PATH, OIDC_CALLBACK_PATH, OIDC_LOGOUT_PATH].includes(path.replace(/\/$/, ""));
}
